import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { CalendarOff, Plus, Trash2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" };
const TYPE_LABEL = { ferie: "Ferie", riposo: "Riposo", permesso: "Permesso", malattia: "Malattia" };

export default function Leaves() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "riposo", date_from: "", date_to: "", reason: "" });
  const [decideId, setDecideId] = useState(null);
  const [decideNote, setDecideNote] = useState("");

  const load = () => api.get("/leave-requests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leave-requests", form);
      toast.success("Richiesta inviata");
      setOpen(false);
      setForm({ type: "riposo", date_from: "", date_to: "", reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/leave-requests/${id}/decision`, { status, admin_note: decideNote });
      toast.success(status === "approved" ? "Approvata" : "Rifiutata");
      setDecideId(null); setDecideNote("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare la richiesta?")) return;
    try { await api.delete(`/leave-requests/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Presenze</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Ferie & Riposo</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {isAdmin ? "Approva o rifiuta le richieste di giorno libero." : "Richiedi un giorno di riposo o ferie."}
          </p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="request-leave-button">
            <Plus className="w-4 h-4 mr-2" /> Richiesta Riposo
          </Button>
        )}
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="leaves-list">
        {list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">Nessuna richiesta.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((r) => (
              <li key={r.id} className="p-4 flex items-center gap-4" data-testid={`leave-${r.id}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                  r.status === "approved" ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                  : r.status === "rejected" ? "bg-rose-50 border-rose-100 text-rose-700"
                  : "bg-amber-50 border-amber-100 text-amber-700"
                }`}>
                  {r.status === "approved" ? <CheckCircle2 className="w-5 h-5" />
                   : r.status === "rejected" ? <XCircle className="w-5 h-5" />
                   : <Clock className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {r.user_name}
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      r.status === "approved" ? "bg-emerald-100 text-emerald-700"
                      : r.status === "rejected" ? "bg-rose-100 text-rose-700"
                      : "bg-amber-100 text-amber-700"
                    }`}>{STATUS_LABEL[r.status]}</span>
                  </div>
                  <div className="font-mono text-xs text-slate-500 mt-0.5">
                    {TYPE_LABEL[r.type] || "Riposo"} · Dal {r.date_from} al {r.date_to}
                    {r.reason && <> · {r.reason}</>}
                  </div>
                  {r.admin_note && (
                    <div className="text-xs text-slate-600 italic mt-1">Nota admin: {r.admin_note}</div>
                  )}
                </div>
                {isAdmin && r.status === "pending" && (
                  <>
                    <button onClick={() => setDecideId({ id: r.id, action: "approve" })}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-semibold"
                      data-testid={`approve-leave-${r.id}`}>Approva</button>
                    <button onClick={() => setDecideId({ id: r.id, action: "reject" })}
                      className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-2 rounded-lg font-semibold"
                      data-testid={`reject-leave-${r.id}`}>Rifiuta</button>
                  </>
                )}
                {(isAdmin || user?.id === r.user_id) && r.status === "pending" && (
                  <button onClick={() => remove(r.id)} className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Request leave dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Richiesta Ferie / Riposo</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
            <div>
              <Label>Tipo</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="leave-type-select">
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dal</Label>
                <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} required data-testid="leave-from-input" />
              </div>
              <div><Label>Al</Label>
                <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} required data-testid="leave-to-input" />
              </div>
            </div>
            <div>
              <Label>Motivo (opz.)</Label>
              <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="submit-leave-button">Invia</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin decision dialog */}
      <Dialog open={!!decideId} onOpenChange={(o) => !o && setDecideId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">
              {decideId?.action === "approve" ? "Approvare la richiesta?" : "Rifiutare la richiesta?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Nota per il dipendente (opz.)</Label>
            <Textarea rows={3} value={decideNote} onChange={(e) => setDecideNote(e.target.value)} data-testid="leave-decision-note" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDecideId(null)}>Annulla</Button>
              <Button
                onClick={() => decide(decideId.id, decideId.action === "approve" ? "approved" : "rejected")}
                className={decideId?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                data-testid="confirm-leave-decision"
              >
                Conferma
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
